import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function AdminMarquee() {
  // ===== State for items list =====
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);

  // ===== Form fields =====
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("#");
  const [sortOrder, setSortOrder] = useState("0");
  const [isActive, setIsActive] = useState(true);

  // ===== Media =====
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  
  // ===== Drag and drop state =====
  const [isDragActive, setIsDragActive] = useState(false);
  const dropZoneRef = useRef(null);

  // ===== UI state =====
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });

  // ===== Best Sellers state =====
  const [loadingBestSellers, setLoadingBestSellers] = useState(false);
  const [showBestSellersModal, setShowBestSellersModal] = useState(false);
  const [bestSellers, setBestSellers] = useState([]);
  const [selectedBestSellers, setSelectedBestSellers] = useState([]);

  // ===== GSAP Refs =====
  const pageRef = useRef(null);
  const modalRef = useRef(null);

  // ===== Helpers =====
  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    gsap.fromTo(
      ".toast-anim",
      { y: 12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.25, ease: "power2.out" }
    );
    
    // Clear previous timeout
    if (showToast.timeoutId) {
      clearTimeout(showToast.timeoutId);
    }
    
    // Set new timeout
    showToast.timeoutId = setTimeout(() => {
      setToast({ show: false, type: "info", msg: "" });
    }, 2600);
  };

  // ===== Fetch items =====
  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await $.getJSON(`${ApiURl}/getMarqueeItems.php?admin=true`);
      if (response?.success) {
        setItems(response.items || []);
        // Update sortOrder default for new items
        setSortOrder((response.items?.length || 0).toString());
      } else {
        showToast("error", response?.message || "Failed to load items");
      }
    } catch (error) {
      console.error("Error fetching marquee items:", error);
      showToast("error", "Failed to load items. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // ===== Reset form =====
  const resetForm = () => {
    setTitle("");
    setLink("#");
    setSortOrder(items.length.toString());
    setIsActive(true);
    removeImage();
    setEditingItem(null);
  };

  // ===== Edit item =====
  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title || "");
    setLink(item.link || "#");
    setSortOrder(item.sort_order?.toString() || "0");
    setIsActive(item.is_active == 1);
    
    // For existing items, we just show the image URL, no file upload
    setImagePreviewUrl(item.image_url);
    setImageFile(null);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== Delete item =====
  // ===== Delete item =====
const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this item?")) return;

  try {
    console.log('Deleting item with ID:', id); // Debug log
    
    const response = await $.ajax({
      url: `${ApiURl}/deleteMarqueeItem.php?id=${id}`, // Send ID in URL
      type: 'DELETE',  // Keep as DELETE method
      dataType: 'json'
    });

    if (response?.success) {
      showToast("success", "Item deleted successfully");
      await fetchItems();
    } else {
      showToast("error", response?.message || "Failed to delete item");
    }
  } catch (error) {
    console.error("Error deleting item:", error);
    console.log('Error response:', error.responseJSON); // Debug log
    showToast("error", "Server error while deleting item");
  }
};

  // ===== Drag and drop handlers =====
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragActive(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragActive(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // ===== Process file =====
  const processFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("error", "Please select a valid image file.");
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      showToast("error", "File too large. Max 2MB for images.");
      return;
    }

    // Clean up previous preview URL
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    const url = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreviewUrl(url);
  };

  // ===== Handle file upload =====
  const handleImageUpload = (e) => {
    const file = (e.target.files && e.target.files[0]) || null;
    if (!file) return;

    processFile(file);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = () => {
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageFile(null);
    setImagePreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ===== Submit form =====
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      showToast("error", "Please enter a title.");
      return;
    }

    // For new items, require image upload
    if (!editingItem && !imageFile) {
      showToast("error", "Please upload an image.");
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    
    // Only append image if a new file is selected
    if (imageFile) {
      formData.append("image", imageFile);
    }
    
    formData.append("title", title.trim());
    formData.append("link", link || "#");
    formData.append("sort_order", parseInt(sortOrder) || 0);
    formData.append("is_active", isActive ? "1" : "0");

    const url = editingItem 
      ? `${ApiURl}/updateMarqueeItem.php?id=${editingItem.id}`
      : `${ApiURl}/addMarqueeItem.php`;

    try {
      const response = await $.ajax({
        url: url,
        type: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json"
      });

      if (response?.success) {
        showToast("success", editingItem ? "Item updated successfully" : "Item added successfully");
        resetForm();
        await fetchItems();
      } else {
        showToast("error", response?.message || "Failed to save item");
      }
    } catch (error) {
      console.error("Error saving item:", error);
      showToast("error", "Server error while saving item");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Move item up/down =====
  const moveItem = async (id, direction) => {
    const currentIndex = items.findIndex(item => item.id === id);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Create a copy of items array
    const updatedItems = [...items];
    const item1 = { ...updatedItems[currentIndex] };
    const item2 = { ...updatedItems[newIndex] };
    
    // Swap sort orders
    const tempOrder = item1.sort_order;
    item1.sort_order = item2.sort_order;
    item2.sort_order = tempOrder;
    
    // Update local state immediately for better UX
    updatedItems[currentIndex] = item1;
    updatedItems[newIndex] = item2;
    setItems(updatedItems);
    
    try {
      // Update both items in database
      await Promise.all([
        $.ajax({
          url: `${ApiURl}/updateMarqueeItem.php?id=${item1.id}`,
          type: 'POST',
          data: { 
            title: item1.title,
            link: item1.link,
            sort_order: item1.sort_order,
            is_active: item1.is_active ? 1 : 0
          },
          dataType: 'json'
        }),
        $.ajax({
          url: `${ApiURl}/updateMarqueeItem.php?id=${item2.id}`,
          type: 'POST',
          data: { 
            title: item2.title,
            link: item2.link,
            sort_order: item2.sort_order,
            is_active: item2.is_active ? 1 : 0
          },
          dataType: 'json'
        })
      ]);
      
      showToast("success", "Item order updated");
      // Refresh to ensure consistency
      await fetchItems();
    } catch (error) {
      console.error("Error reordering items:", error);
      showToast("error", "Failed to reorder items");
      // Revert to original order
      await fetchItems();
    }
  };

// ===== Fetch Best Selling Products =====
const fetchBestSellers = async () => {
  setLoadingBestSellers(true);
  try {
    const response = await $.getJSON(`${ApiURl}/getBestSellingProducts.php`);
    
    if (response?.success) {
      if (response.products && response.products.length > 0) {
        // Transform the data to match our format
        const products = response.products.map(product => {
          // Build the full image URL
          let imageUrl = '';
          
          if (product.image) {
            // Clean the filename - remove any remaining brackets
            const cleanFilename = product.image.replace(/[\[\]"]/g, '');
            
            // Build the URL - assuming images are stored in an 'images' folder
            // You might need to adjust this path based on where your images are actually stored
            imageUrl = `${ApiURl}/images/${cleanFilename}`;
            
            // Log for debugging
            console.log('Product image:', {
              original: product.image,
              cleaned: cleanFilename,
              url: imageUrl
            });
          } else {
            imageUrl = 'https://via.placeholder.com/300?text=No+Image';
          }
          
          return {
            id: product.id,
            title: product.name,
            image_url: imageUrl,
            link: product.link || `/product/${product.id}`,
            total_sold: product.total_sold || 0,
            order_count: product.order_count || 0,
            selected: false
          };
        });
        
        setBestSellers(products);
        setSelectedBestSellers([]);
        setShowBestSellersModal(true);
        
        // Animate modal
        setTimeout(() => {
          if (modalRef.current) {
            gsap.fromTo(modalRef.current, 
              { scale: 0.95, opacity: 0, y: 20 }, 
              { scale: 1, opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }
            );
          }
        }, 10);
      } else {
        showToast("info", "No best selling products found. Make sure you have completed orders.");
      }
    } else {
      showToast("error", response?.message || "Failed to fetch best selling products");
    }
  } catch (error) {
    console.error("Error fetching best sellers:", error);
    showToast("error", "Server error while fetching best sellers");
  } finally {
    setLoadingBestSellers(false);
  }
};

  // ===== Toggle product selection =====
  const toggleProductSelection = (productId) => {
    setBestSellers(prev => {
      const updatedProducts = prev.map(product => 
        product.id === productId 
          ? { ...product, selected: !product.selected } 
          : product
      );
      
      // Update selected array based on the new state
      const newlySelected = updatedProducts.filter(p => p.selected);
      setSelectedBestSellers(newlySelected);
      
      return updatedProducts;
    });
  };

  // ===== Select/Deselect All =====
  const toggleSelectAll = () => {
    const allSelected = bestSellers.length > 0 && bestSellers.every(p => p.selected);
    
    setBestSellers(prev => {
      const updatedProducts = prev.map(product => ({ 
        ...product, 
        selected: !allSelected 
      }));
      
      // Update selected array
      if (!allSelected) {
        setSelectedBestSellers([...updatedProducts]);
      } else {
        setSelectedBestSellers([]);
      }
      
      return updatedProducts;
    });
  };

 // ===== Add Selected Best Sellers =====
const addSelectedBestSellers = async () => {
  if (selectedBestSellers.length === 0) {
    showToast("error", "Please select at least one product");
    return;
  }

  setLoadingBestSellers(true);
  
  // Extract just the filename from the image URL
  const productsToAdd = selectedBestSellers.map(p => {
    // Get the filename from the URL
    let filename = '';
    
    if (p.image_url) {
      // Extract filename from URL
      const urlParts = p.image_url.split('/');
      filename = urlParts[urlParts.length - 1];
      
      // Remove any query parameters
      filename = filename.split('?')[0];
      
      // Remove any brackets (just in case)
      filename = filename.replace(/[\[\]"]/g, '');
    }
    
    return {
      title: p.title,
      image_path: filename, // Just the filename, no path
      link: p.link
    };
  });

  console.log('Products to add:', productsToAdd); // For debugging

  try {
    const response = await $.ajax({
      url: `${ApiURl}/addMultipleMarqueeItems.php`,
      type: "POST",
      data: JSON.stringify({ 
        products: productsToAdd,
        maxItems: 10 
      }),
      contentType: "application/json",
      dataType: "json"
    });

    if (response?.success) {
      showToast("success", `Added ${response.total_added} best selling items`);
      setShowBestSellersModal(false);
      await fetchItems();
    } else {
      showToast("error", response?.message || "Failed to add items");
    }
  } catch (error) {
    console.error("Error adding best sellers:", error);
    showToast("error", "Server error while adding items");
  } finally {
    setLoadingBestSellers(false);
  }
};

  // ===== Close Modal =====
  const closeModal = () => {
    if (modalRef.current) {
      gsap.to(modalRef.current, {
        scale: 0.95,
        opacity: 0,
        y: 20,
        duration: 0.2,
        ease: "power2.in",
        onComplete: () => {
          setShowBestSellersModal(false);
          setBestSellers([]);
          setSelectedBestSellers([]);
        }
      });
    } else {
      setShowBestSellersModal(false);
      setBestSellers([]);
      setSelectedBestSellers([]);
    }
  };

  return (
    <div ref={pageRef} className="w-full font-['Poppins',sans-serif] bg-gray-50 min-h-screen">
      {/* Toast */}
      {toast.show && (
        <div className="toast-anim fixed bottom-5 right-5 z-[9999]">
          <div
            className={[
              "rounded-xl px-4 py-3 shadow-lg border text-sm",
              toast.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : toast.type === "error"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "bg-slate-50 border-slate-200 text-slate-900",
            ].join(" ")}
          >
            {toast.msg}
          </div>
        </div>
      )}

      {/* Best Sellers Modal */}
      {showBestSellersModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] p-4">
          <div 
            ref={modalRef}
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
          >
            <div className="px-6 py-5 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Best Selling Products</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Select products to add to marquee (based on order history)
                </p>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
              {loadingBestSellers ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-[#b29063]"></div>
                  <p className="text-slate-600 mt-4">Loading best sellers...</p>
                </div>
              ) : bestSellers.length === 0 ? (
                <div className="text-center py-12 text-slate-600">
                  <p>No best selling products found.</p>
                  <p className="text-sm mt-2">Make sure you have completed orders in your database.</p>
                </div>
              ) : (
                <>
                  {/* Select All Header */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bestSellers.length > 0 && bestSellers.every(p => p.selected)}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 text-[#b29063] focus:ring-[#b29063]"
                      />
                      <span className="text-sm font-medium text-slate-700">Select All</span>
                    </label>
                    <span className="text-sm text-slate-500">
                      {selectedBestSellers.length} of {bestSellers.length} selected
                    </span>
                  </div>

                  {/* Products List */}
                  <div className="space-y-3">
                    {bestSellers.map((product) => (
                      <div
                        key={product.id}
                        className={`flex items-center gap-4 p-3 rounded-xl border transition ${
                          product.selected 
                            ? 'bg-[#b29063]/5 border-[#b29063]' 
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={product.selected || false}
                          onChange={() => toggleProductSelection(product.id)}
                          className="w-4 h-4 rounded border-slate-300 text-[#b29063] focus:ring-[#b29063] flex-shrink-0"
                        />
                        
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
                          <img 
                            src={product.image_url} 
                            alt={product.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/64?text=No+Image';
                            }}
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 truncate">{product.title}</h3>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Sold: {product.total_sold}
                            </span>
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              Orders: {product.order_count}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={addSelectedBestSellers}
                disabled={selectedBestSellers.length === 0 || loadingBestSellers}
                className={[
                  "px-4 py-2 text-sm font-medium text-white rounded-lg transition flex items-center gap-2",
                  selectedBestSellers.length === 0 || loadingBestSellers
                    ? "bg-slate-400 cursor-not-allowed"
                    : "bg-[#b29063] hover:bg-[#a08053]"
                ].join(" ")}
              >
                {loadingBestSellers ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                    Adding...
                  </>
                ) : (
                  `Add Selected (${selectedBestSellers.length})`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Marquee Items Management
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage the scrolling marquee items on the homepage.
          </p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {items.length} Items
            </div>
            
            {/* Button for Best Sellers */}
            <button
              onClick={fetchBestSellers}
              disabled={loadingBestSellers}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 2L15 9H22L16 14L19 21L12 16.5L5 21L8 14L2 9H9L12 2Z" strokeWidth="2" fill="none"/>
              </svg>
              {loadingBestSellers ? 'Loading...' : 'Add Best Sellers'}
            </button>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-8">
          <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <h2 className="text-lg font-semibold text-slate-900">
              {editingItem ? "Edit Item" : "Add New Item"}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Form Fields */}
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-900">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    placeholder="Item title"
                    maxLength="100"
                    required
                  />
                </div>

                {/* Link */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-900">
                    Link URL <span className="text-slate-400">(Optional)</span>
                  </label>
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Sort Order & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="0"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Lower numbers appear first
                    </p>
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">Status</label>
                    <div className="flex items-center h-[42px]">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-[#b29063] focus:ring-[#b29063]"
                        />
                        <span className="text-sm text-slate-700">Active</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - File Upload */}
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-900">
                  Image {!editingItem && <span className="text-red-500">*</span>}
                  {editingItem && <span className="text-slate-400 ml-1">(Leave empty to keep current)</span>}
                </label>
                
                {/* Drag & Drop Zone */}
                <div
                  ref={dropZoneRef}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`flex flex-col items-center justify-center w-full rounded-2xl cursor-pointer border-2 border-dashed transition p-8 text-center mb-3 ${
                    isDragActive
                      ? "border-[#b29063] bg-[#b29063]/5"
                      : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="h-12 w-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-slate-600">
                        <path d="M12 16V8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path
                          d="M20 16.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M7 10.5 12 6l5 4.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">
                        {isDragActive ? "Drop it here!" : "Click to upload or drag and drop"}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">Images only (JPEG, PNG, GIF, WebP)</p>
                    <p className="text-xs text-slate-400">Max size: 2MB</p>
                  </div>

                  <input
                    ref={fileInputRef}
                    id="item-image"
                    type="file"
                    className="hidden"
                    onChange={handleImageUpload}
                    accept="image/*"
                  />
                </div>

                {imagePreviewUrl && (
                  <div className="mt-3">
                    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                      <div className="relative">
                        <img
                          src={imagePreviewUrl}
                          alt="Preview"
                          className="w-full h-48 object-cover bg-white"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300?text=Image+Error';
                          }}
                        />

                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 rounded-full px-3 py-1 text-xs bg-white/90 border border-slate-200 hover:bg-slate-50 transition shadow-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className={[
                      "flex-1 inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold text-white transition",
                      submitting 
                        ? "bg-slate-400 cursor-not-allowed" 
                        : "bg-[#b29063] hover:bg-[#a08053] active:scale-[0.99]",
                    ].join(" ")}
                  >
                    {submitting ? "Saving..." : (editingItem ? "Update Item" : "Add Item")}
                  </button>
                  
                  {editingItem && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-5 py-3 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-6 py-5 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Current Items</h2>
            <p className="text-sm text-slate-600 mt-1">Use arrows to reorder items</p>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8 text-slate-600">Loading items...</div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-slate-600">No items added yet.</div>
            ) : (
              <div className="space-y-4">
                {items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((item, index) => (
                  <div
                    key={item.id}
                    className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-xl border transition ${
                      !item.is_active 
                        ? "bg-slate-50 border-slate-200 opacity-75" 
                        : "bg-white border-slate-200 hover:shadow-sm"
                    }`}
                  >
                    {/* Reorder Arrows */}
                    <div className="flex sm:flex-col gap-1">
                      <button
                        onClick={() => moveItem(item.id, 'up')}
                        disabled={index === 0}
                        className={[
                          "w-8 h-8 flex items-center justify-center rounded-lg border transition",
                          index === 0
                            ? "border-slate-200 text-slate-300 cursor-not-allowed" 
                            : "border-slate-300 text-slate-600 hover:bg-white hover:border-[#b29063] hover:text-[#b29063]"
                        ].join(" ")}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveItem(item.id, 'down')}
                        disabled={index === items.length - 1}
                        className={[
                          "w-8 h-8 flex items-center justify-center rounded-lg border transition",
                          index === items.length - 1
                            ? "border-slate-200 text-slate-300 cursor-not-allowed" 
                            : "border-slate-300 text-slate-600 hover:bg-white hover:border-[#b29063] hover:text-[#b29063]"
                        ].join(" ")}
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>

                    {/* Preview */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                      <img 
                        src={item.image_url} 
                        alt={item.title} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/96?text=No+Image';
                        }}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-slate-900 truncate">{item.title}</h3>
                        <span className={[
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          item.is_active 
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        ].join(" ")}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          #{item.sort_order || 0}
                        </span>
                      </div>
                      {item.link && item.link !== '#' && (
                        <p className="text-xs text-slate-500 mt-1 truncate">
                          Link: {item.link}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-auto flex-shrink-0">
                      <button
                        onClick={() => handleEdit(item)}
                        className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminMarquee;